import {
  ServiceEventFormDefaultValues,
  ServiceEventFormProps,
  ServiceEventFormRawValues,
  ServiceEventFormType
} from "./ServiceEventForm";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {
  adjustFromTimeZoneSafely,
  isBefore,
  isStringUndefinedOrBlank,
  serviceEventCloseoutPolicy,
  ServiceEventEntity
} from "@ots-frontend/ots-data";
import {DefaultTFuncReturn} from "i18next";
import { useAuth } from "../../../providers/AuthProvider/AuthProvider";

export const toFormDefaultsFromServiceEvent = (serviceEvent: ServiceEventEntity | undefined): ServiceEventFormDefaultValues => {
  return {
    eventType: serviceEvent?.eventType,
    name: serviceEvent?.name,
    severityLevel: serviceEvent?.severityLevel,
    startDate: adjustFromTimeZoneSafely(serviceEvent?.startDttm),
    endDate: adjustFromTimeZoneSafely(serviceEvent?.endDttm),
    closeoutPolicy: serviceEvent?.closeoutPolicy,
    note: serviceEvent?.note,
    targets: serviceEvent?.targets?.map((t) => ({
      objectId: t.objectId,
      objectType: t.objectType,
      mode: t.targetMode,
    })) ?? []
  };
};

const getInitialValues = (defaultValues: ServiceEventFormDefaultValues | undefined): ServiceEventFormRawValues => {
  return {
    eventType: defaultValues?.eventType?.code,
    name: defaultValues?.name,
    severityLevel: defaultValues?.severityLevel,
    startDate: defaultValues?.startDate,
    endDate: defaultValues?.endDate,
    closeoutPolicy: defaultValues?.closeoutPolicy?.code,
    note: defaultValues?.note,
    targets: defaultValues?.targets?.map((t) => ({
      objectId: t.objectId,
      objectType: t.objectType,
      mode: t.mode.code,
    })) ?? []
  }
}

export type UseServiceEventFormProps = Pick<ServiceEventFormProps, 'defaultValues'> & {}

export function useServiceEventForm(props: UseServiceEventFormProps): ServiceEventFormType {
  const {
    defaultValues
  } = props

  const {companyId} = useAuth()

  const {t} = useTranslation(["common"])

  const form = useForm({
    initialValues: getInitialValues({
      ...defaultValues,
      company: defaultValues?.company ?? companyId,
      closeoutPolicy: defaultValues.closeoutPolicy ?? serviceEventCloseoutPolicy.toBaseline,
    }),
    validate: {
      eventType: (value) => value ? undefined : t("common.validation.required", "Required"),
      company: (value) => value ? undefined : t("common.validation.required", "Required"),
      name: (value) => !isStringUndefinedOrBlank(value) ? undefined : t("common.validation.required", "Required"),
      startDate: (value) => value ? undefined : t("common.validation.required", "Required"),
      endDate: (value, values) => {
        let error: string | DefaultTFuncReturn | undefined = undefined

        if (value && values.startDate && isBefore(value, values.startDate)) {
          error = t<string>("common.form.validation.enddatebefore", "The end date can't be before the start date")
        }

        return error
      },
      closeoutPolicy: (value) => value ? undefined : t("common.validation.required", "Required"),
    },
  })

  return form
}
